import { z } from "zod";

import { PropertySchemaType } from "~/common/types";

const PropertySchemaTypeSchema = z.nativeEnum(PropertySchemaType);

const PropertySchemaColumnSchema = z
  .object({
    position: z.number().int().min(0),
    name: z.string().min(1),
    type: PropertySchemaTypeSchema,
    description: z.string().min(1),
    required: z.boolean(),
    unique: z.boolean(),
    // NOTE: remove fixed URL_allow_list_* fields; we’ll validate dynamic keys in superRefine
  })
  // keep unknown keys so we can read dynamic keys like select_option_* and URL_allow_list_*
  .passthrough()
  .superRefine((data, ctx) => {
    // ---------- URL allow-list (dynamic) ----------
    // Keys like: URL_allow_list_{index}_{value|regex}
    const urlMap = new Map<number, { value?: unknown; regex?: unknown }>();
    for (const [key, val] of Object.entries(data)) {
      const m = key.match(/^URL_allow_list_(\d+)_(value|regex)$/);
      if (!m) continue;
      const idx = Number(m[1]);
      const kind = m[2] as "value" | "regex";
      if (!urlMap.has(idx)) urlMap.set(idx, {});
      const option = urlMap.get(idx);
      if (option) {
        option[kind] = val ?? null;
      }
    }

    const urlPairs = [...urlMap.entries()].sort((a, b) => a[0] - b[0]).map(([i, p]) => ({ index: i, ...p }));

    if (data.type === "url") {
      // Must have at least one filled entry (value or regex)
      const filled = urlPairs.filter((p) => {
        const hasValue = typeof p.value === "string" && p.value.trim() !== "";
        const hasRegex = typeof p.regex === "string" && p.regex.trim() !== "";
        return hasValue || hasRegex;
      });

      if (filled.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "For 'url' type, provide at least one URL allow-list item: URL_allow_list_{n}_value or URL_allow_list_{n}_regex.",
          path: [], // whole object
        });
      }

      // Validate each filled entry
      for (const p of filled) {
        const hasValue = typeof p.value === "string" && p.value.trim() !== "";
        const hasRegex = typeof p.regex === "string" && p.regex.trim() !== "";

        if (hasValue) {
          try {
            // validate URL syntax
            new URL(p.value as string);
          } catch {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `URL_allow_list_${p.index}_value must be a valid URL.`,
              path: [`URL_allow_list_${p.index}_value`],
            });
          }
        }

        if (hasRegex) {
          try {
            // validate regex pattern
            // if you allow flags, change key to *_regex_flags and compile with them
            // e.g., new RegExp(pattern, flags)
            // For now we just ensure the pattern compiles.
            new RegExp(p.regex as string);
          } catch {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `URL_allow_list_${p.index}_regex must be a valid regular expression.`,
              path: [`URL_allow_list_${p.index}_regex`],
            });
          }
        }
      }
    }

    // ---------- Dynamic select options ----------
    // Keys like: select_option_{index}_{value|label}
    const optionMap = new Map<number, { value?: string | null; label?: string | null }>();
    for (const [key, val] of Object.entries(data)) {
      const m = key.match(/^select_option_(\d+)_(value|label)$/);
      if (!m) continue;
      const idx = Number(m[1]);
      const kind = m[2] as "value" | "label";

      if (!optionMap.has(idx)) optionMap.set(idx, {});

      const option = optionMap.get(idx);
      if (option) {
        option[kind] = (val as string | null | undefined) ?? null;
      }
    }

    if (data.type === "select" || data.type === "multiSelect") {
      const pairs = [...optionMap.entries()].sort((a, b) => a[0] - b[0]).map(([i, p]) => ({ index: i, ...p }));

      // Consider a pair "filled" if either side is non-empty string
      const filled = pairs.filter(
        (p) =>
          (typeof p.value === "string" && p.value.trim() !== "") ||
          (typeof p.label === "string" && p.label.trim() !== ""),
      );

      if (filled.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Provide at least one option (value + label) for select/multiSelect.",
          path: [],
        });
      }

      // Each filled pair must have both value and label (non-empty)
      for (const p of filled) {
        const valueOk = typeof p.value === "string" && p.value.trim() !== "";
        const labelOk = typeof p.label === "string" && p.label.trim() !== "";
        if (!valueOk || !labelOk) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Option ${p.index} must have both value and label.`,
            path: [`select_option_${p.index}_${!valueOk ? "value" : "label"}`],
          });
        }
      }
    }
  });

export const PropertySchemaColumnsSchema = z
  .array(z.union([PropertySchemaColumnSchema, z.null(), z.undefined()]))
  .transform((items) => items.filter(Boolean) as z.infer<typeof PropertySchemaColumnSchema>[]);

export type PropertySchemaColumn = z.infer<typeof PropertySchemaColumnSchema> & Record<string, unknown>;
